import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  PrimaryButton,
  DefaultButton,
  Link,
  makeStyles,
  createStyles,
  Theme,
} from '@bb-ui/react-library';
import { Warning } from '@bb-ui/icons/dist/small/Warning';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    reminderSection: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(3),
      border: `1px solid ${theme.palette.grey[200]}`,
    },
    reminderText: {
      color: theme.palette.text.primary,
      fontWeight: 600,
      marginBottom: theme.spacing(2),
    },
    linkSection: {
      marginTop: theme.spacing(1),
    },
    linkText: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    dialogTitleWithIcon: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    warningIcon: {
      color: theme.palette.warning.main,
      fontSize: '24px',
    },
  }),
);

export interface FeatureFlagEditWarningDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
}

export const FeatureFlagEditWarningDialog: React.FC<FeatureFlagEditWarningDialogProps> = ({
  isOpen,
  onClose,
  onContinue,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      data-testid="feature-flag-edit-warning-dialog"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle onClose={onClose} closeLabel={t('global.close')}>
        <div className={classes.dialogTitleWithIcon}>
          <Warning className={classes.warningIcon} />
          {t('featureFlagDefinitionInfo.warningDialogTitle')}
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={classes.reminderSection} data-testid="reminder-section">
          <Typography variant="body1" className={classes.reminderText} data-testid="reminder-text">
            {t('featureFlagDefinitionInfo.warningReminderText')}
          </Typography>
          <div className={classes.linkSection}>
            <Typography variant="body2" className={classes.linkText}>
              {t('featureFlagDefinitionInfo.documentationLinkText')}
            </Typography>
            <Link
              href="https://anthologyinc.sharepoint.com/sites/bb-lrnctl/SitePages/HOW-TO---Getting-Started-with-Foundations-Feature-Flags-UI-for-Feature-Flags-Use-Cases.aspx"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('featureFlagDefinitionInfo.featureFlagDocumentationLink')}
            </Link>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <DefaultButton onClick={onClose}>{t('global.cancel')}</DefaultButton>
        <PrimaryButton onClick={onContinue}>{t('global.continue')}</PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};
